import React from 'react';
import { Badge, Box, Button, Card, CardBody, CardHeader, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { FaPencilAlt } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { getDisplayRole, getDisplayRoleLabel } from '../../utils/displayRole';
import TourModeActionButton from './TourModeActionButton';

const formatUserLabel = (user) => {
  if (!user) return '';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName || user.email || 'User';
};

const getUserRoleLabel = (user) => {
  const assignments = Array.isArray(user?.rbac?.assignments) ? user.rbac.assignments : [];

  if (assignments.length > 0) {
    const labels = Array.from(
      new Set(
        assignments
          .map((assignment) => assignment?.role_name ?? assignment?.role?.name ?? assignment?.name)
          .filter(Boolean)
          .map(getDisplayRoleLabel)
      )
    );

    if (labels.length > 0) return labels.join(', ');
  }

  return getDisplayRole(user) || 'User';
};

const OrganizationRelationshipsSection = ({
  users = [],
  networks = [],
  onUserAction,
  userActionLabel,
  isTour = false,
  getUserEditPath,
  onNetworkAction,
  networkActionLabel,
  disableNetworkAction = false,
}) => (
  <Card>
    <CardHeader pb={2}>
      <HStack justify="space-between" flexWrap="wrap" spacing={2}>
        <Heading size="md">Relationships</Heading>
        <HStack flexWrap="wrap" spacing={2}>
          {userActionLabel ? (
            <TourModeActionButton size="sm" variant="outline" onClick={onUserAction} isTour={isTour}>
              {userActionLabel}
            </TourModeActionButton>
          ) : null}
          {networkActionLabel ? (
            <Button size="sm" variant="outline" onClick={onNetworkAction} isDisabled={disableNetworkAction}>
              {networkActionLabel}
            </Button>
          ) : null}
        </HStack>
      </HStack>
    </CardHeader>
    <CardBody pt={2}>
      <VStack align="start" spacing={4}>
        <Box>
          <Text fontWeight="semibold" mb={1}>Users</Text>
          {users.length > 0 ? (
            <VStack align="stretch" spacing={2}>
              {users.map((user, index) => (
                <Box
                  key={user.id || user.email || `${formatUserLabel(user)}-${index}`}
                  borderWidth="1px"
                  borderRadius="md"
                  px={3}
                  py={2}
                >
                  <HStack justify="space-between" align="start" spacing={3}>
                    <Box>
                      <Text fontWeight="medium">{formatUserLabel(user)}</Text>
                      <Text fontSize="sm" color="gray.600">{user.email || 'No email provided'}</Text>
                      <Text fontSize="sm" color="gray.600">{getUserRoleLabel(user)}</Text>
                    </Box>
                    {typeof getUserEditPath === 'function' && user?.id ? (
                      <Button
                        as={RouterLink}
                        to={getUserEditPath(user)}
                        size="sm"
                        variant="outline"
                        leftIcon={<FaPencilAlt />}
                      >
                        Edit
                      </Button>
                    ) : null}
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : (
            <Text>No school users assigned</Text>
          )}
        </Box>
        <Box>
          <Text fontWeight="semibold" mb={1}>Network</Text>
          {networks.length > 0 ? (
            <VStack align="start" spacing={1}>
              {networks.map((network, index) => (
                <Badge key={network.id || `${network.name}-${index}`} colorScheme="blue" variant="subtle">
                  {network.name}
                </Badge>
              ))}
            </VStack>
          ) : (
            <Text as='i'>None</Text>
          )}
        </Box>
      </VStack>
    </CardBody>
  </Card>
);

export default OrganizationRelationshipsSection;
